/**
 * YAML frontmatter — parsing, and *conservative* writing.
 *
 * Frontmatter is where a note's metadata has to live if it is to survive a
 * round-trip to disk: a `.md` file in a vault carries its own tags and aliases,
 * whereas anything stored only in IndexedDB is lost the moment the file is
 * opened by another tool. So this is the home of `tags`, `aliases`, `created`
 * and whatever else the user puts there.
 *
 * ## Why not a YAML library
 *
 * We parse a deliberate subset (top-level scalars, flow and block sequences) and
 * treat everything else as opaque. That is not a shortcut around bundle size
 * alone: a general YAML round-trip *rewrites* the document — reordering keys,
 * dropping comments, re-quoting strings. Users hand-write this block, so writing
 * is line-based instead. We patch only the keys we are asked to change and leave
 * every other byte, including nested maps and comments we do not understand,
 * exactly where it was. Anything we cannot parse we also refuse to destroy.
 */

/** A value we can round-trip. Anything richer is preserved but not exposed. */
export type FrontmatterValue = string | number | boolean | null | string[];

/** Parsed frontmatter plus the body that followed it. */
export interface ParsedFrontmatter {
  /** Top-level keys we understood. Unparsed structures are absent. */
  data: Record<string, FrontmatterValue>;
  /** The YAML source between the fences, without them. `null` = no block. */
  raw: string | null;
  /** The markdown after the closing fence. */
  body: string;
}

/** A frontmatter block must open on the very first line. */
const OPEN_RE = /^---[ \t]*\r?\n/;

/**
 * Split a document into its frontmatter block and body.
 *
 * A block only counts when `---` is the first line and a closing `---` (or
 * `...`) follows. An unterminated block is not frontmatter — it is a document
 * that happens to start with a horizontal rule, and treating it otherwise would
 * swallow the user's text.
 */
export function splitFrontmatter(markdown: string): {
  raw: string | null;
  body: string;
} {
  const open = OPEN_RE.exec(markdown);
  if (!open) return { raw: null, body: markdown };

  const rest = markdown.slice(open[0].length);
  const close = /^(?:---|\.\.\.)[ \t]*(?:\r?\n|$)/m.exec(rest);
  if (!close || close.index === undefined) return { raw: null, body: markdown };

  return {
    raw: rest.slice(0, close.index),
    body: rest.slice(close.index + close[0].length),
  };
}

/** The document body with any frontmatter removed (for search, title, counts). */
export function stripFrontmatter(markdown: string): string {
  return splitFrontmatter(markdown).body;
}

/** Strip a trailing ` # comment` from an unquoted scalar (YAML needs the space). */
function stripComment(s: string): string {
  const m = /(^|\s)#/.exec(s);
  return m && m.index !== undefined ? s.slice(0, m.index).trimEnd() : s;
}

/** Unquote and coerce a scalar. Unquoted `true`/`42`/`null` get their types. */
function parseScalar(rawValue: string): FrontmatterValue {
  const s = rawValue.trim();
  if (s === '') return '';
  const quoted = /^"(.*)"$/.exec(s) ?? /^'(.*)'$/.exec(s);
  if (quoted) {
    const inner = quoted[1] ?? '';
    // Only double quotes carry escapes in YAML; single quotes escape `''`.
    return s.startsWith('"')
      ? inner.replace(/\\"/g, '"').replace(/\\n/g, '\n').replace(/\\\\/g, '\\')
      : inner.replace(/''/g, "'");
  }
  const bare = stripComment(s);
  if (bare === 'true') return true;
  if (bare === 'false') return false;
  if (bare === 'null' || bare === '~') return null;
  if (/^-?\d+(\.\d+)?$/.test(bare)) return Number(bare);
  return bare;
}

/** Split a flow sequence body (`a, "b, c"`) on top-level commas. */
function splitFlow(inner: string): string[] {
  const out: string[] = [];
  let buf = '';
  let quote: '"' | "'" | null = null;
  for (const ch of inner) {
    if (quote) {
      buf += ch;
      if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      buf += ch;
      continue;
    }
    if (ch === ',') {
      out.push(buf);
      buf = '';
      continue;
    }
    buf += ch;
  }
  out.push(buf);
  return out.map((s) => s.trim()).filter((s) => s !== '');
}

/** Coerce a parsed scalar to its string form (list items are always strings). */
function asString(v: FrontmatterValue): string {
  return Array.isArray(v) ? v.join(', ') : v === null ? '' : String(v);
}

/**
 * Parse the subset: top-level `key: scalar`, `key: [a, b]`, and block sequences
 * (`key:` followed by indented `- item` lines). Nested maps are skipped — the
 * key is left out of `data` rather than half-parsed, and stays untouched on write.
 */
export function parseFrontmatter(markdown: string): ParsedFrontmatter {
  const { raw, body } = splitFrontmatter(markdown);
  if (raw === null) return { data: {}, raw: null, body };

  const data: Record<string, FrontmatterValue> = {};
  const lines = raw.split(/\r?\n/);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    if (/^\s*(#|$)/.test(line)) continue; // comment or blank
    const m = /^([A-Za-z0-9_][\w .-]*)\s*:(.*)$/.exec(line);
    if (!m) continue; // indented continuation or something we do not model
    const key = (m[1] ?? '').trim();
    const rest = (m[2] ?? '').trim();

    const flow = /^\[(.*)\]$/.exec(rest);
    if (flow) {
      data[key] = splitFlow(flow[1] ?? '').map((s) => asString(parseScalar(s)));
      continue;
    }

    if (rest === '' || rest.startsWith('#')) {
      // Either a block sequence or a nested map. Look ahead at the indentation.
      const items: string[] = [];
      let j = i + 1;
      for (; j < lines.length; j++) {
        const next = lines[j] ?? '';
        if (/^\s*$/.test(next)) continue;
        const item = /^\s+-\s*(.*)$/.exec(next);
        if (!item) break;
        items.push(asString(parseScalar(item[1] ?? '')));
      }
      // A nested map (indented `k: v`) leaves no items — skip the key entirely.
      if (items.length > 0) {
        data[key] = items;
        i = j - 1;
      }
      continue;
    }

    data[key] = parseScalar(rest);
  }

  return { data, raw, body };
}

/** Read a key as a clean string list, accepting `a, b` shorthand for a list. */
export function readList(data: Record<string, FrontmatterValue>, key: string): string[] {
  const v = data[key];
  if (v === undefined || v === null) return [];
  if (Array.isArray(v)) return v.map((s) => s.trim()).filter(Boolean);
  return String(v)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Characters that force quoting when we emit a scalar. */
const NEEDS_QUOTES = /^[\s>|*&!%@`-]|[:#[\]{},]|\s$|^$/;

function emitScalar(v: string | number | boolean | null): string {
  if (v === null) return 'null';
  if (typeof v !== 'string') return String(v);
  const looksTyped = /^(true|false|null|~|-?\d+(\.\d+)?)$/.test(v);
  if (NEEDS_QUOTES.test(v) || looksTyped) {
    return `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  }
  return v;
}

/** Render one key as the lines it occupies (block sequences for arrays). */
function emitEntry(key: string, value: Exclude<FrontmatterValue, undefined>): string[] {
  if (Array.isArray(value)) {
    return [`${key}:`, ...value.map((item) => `  - ${emitScalar(item)}`)];
  }
  return [`${key}: ${emitScalar(value)}`];
}

/** The line span a top-level key occupies, including its indented continuation. */
function findEntry(lines: string[], key: string): { start: number; end: number } | null {
  const head = new RegExp(`^${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:`);
  for (let i = 0; i < lines.length; i++) {
    if (!head.test(lines[i] ?? '')) continue;
    let end = i + 1;
    while (end < lines.length && /^(\s+\S|\s*$)/.test(lines[end] ?? '')) {
      // A blank line only belongs to the entry if an indented line follows.
      if (/^\s*$/.test(lines[end] ?? '')) {
        const nextReal = lines.slice(end + 1).find((l) => !/^\s*$/.test(l));
        if (nextReal === undefined || !/^\s+\S/.test(nextReal)) break;
      }
      end++;
    }
    return { start: i, end };
  }
  return null;
}

/**
 * Patch top-level keys in a document's frontmatter, creating the block if
 * needed. `undefined` (or an empty array) removes a key. Untouched keys,
 * comments, ordering and formatting survive verbatim.
 *
 * Returns the document unchanged when the patch is a no-op, so callers can use
 * it on every save without dirtying documents that have nothing to record.
 */
export function updateFrontmatter(
  markdown: string,
  patch: Record<string, FrontmatterValue | undefined>,
): string {
  const entries = Object.entries(patch).filter(([, v]) => !(Array.isArray(v) && v.length === 0));
  const removals = entries.filter(([, v]) => v === undefined).map(([k]) => k);
  const writes = entries.filter((e): e is [string, FrontmatterValue] => e[1] !== undefined);
  const emptied = Object.entries(patch)
    .filter(([, v]) => Array.isArray(v) && v.length === 0)
    .map(([k]) => k);
  const drop = [...removals, ...emptied];

  const { raw, body } = splitFrontmatter(markdown);

  if (raw === null) {
    if (writes.length === 0) return markdown;
    const block = writes.flatMap(([k, v]) => emitEntry(k, v));
    return `---\n${block.join('\n')}\n---\n\n${markdown.replace(/^\s*\n/, '')}`;
  }

  const lines = raw.split(/\r?\n/);
  // Drop a trailing empty line produced by the split so we do not accumulate them.
  if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();

  for (const key of drop) {
    const span = findEntry(lines, key);
    if (span) lines.splice(span.start, span.end - span.start);
  }

  for (const [key, value] of writes) {
    const next = emitEntry(key, value);
    const span = findEntry(lines, key);
    if (span) {
      const current = lines.slice(span.start, span.end);
      if (current.join('\n') === next.join('\n')) continue;
      lines.splice(span.start, span.end - span.start, ...next);
    } else {
      lines.push(...next);
    }
  }

  const nextRaw = lines.join('\n');
  if (nextRaw === raw.replace(/\r?\n$/, '')) return markdown;
  if (nextRaw.trim() === '') return body.replace(/^\s*\n/, '');
  // `body` is exactly what followed the closing fence, so no separator is
  // synthesised here: reassembly must not add a blank line the user never had.
  return `---\n${nextRaw}\n---\n${body}`;
}
